import React, { createContext, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';

export const RootNodeRenderer = memo((props: RootNodeRendererProps) => {
	const { batchSize, children, maxRenderIndex, onBatchRendered } = props;

	const seq = useBatchSequence(props);
	const [seqIndex, setSeqIndex] = useState(0);

	const lastSeqIndex = (seq?.length ?? Math.max(Math.ceil(maxRenderIndex / batchSize), 1)) - 1; // The last valid sequence index

	useEffect(() => {
		// eslint-disable-next-line compat/compat
		const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
		const cancelHandle = requestIdleCallback(() => {
			const done = seqIndex >= lastSeqIndex;

			onBatchRendered?.(done);

			if (!done) {
				setSeqIndex((pendingState) =>
					pendingState < lastSeqIndex ? pendingState + 1 : pendingState,
				);
			}
		});

		return () => {
			const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
			cancelIdleCallback(cancelHandle);
		};
	}, [seqIndex, lastSeqIndex, onBatchRendered, setSeqIndex]);

	const maxBatchRenderIndex = (seqIndex + 1) * batchSize;
	const shouldRenderNode = useCallback(
		(renderIndex: number) => {
			if (seq) {
				// New/alternating sequence:
				const batchIndex = Math.floor(renderIndex / batchSize);
				if (batchIndex < seq.length) {
					return seq[batchIndex] <= seqIndex;
				}
			} else {
				// Old/linear sequence:
				return !maxBatchRenderIndex || renderIndex < maxBatchRenderIndex;
			}
		},
		[batchSize, maxBatchRenderIndex, seq, seqIndex],
	);

	return (
		<NodeRendererContext.Provider value={shouldRenderNode}>{children}</NodeRendererContext.Provider>
	);
});

type RootNodeRendererProps = PropsWithChildren<{
	batchSize: number;
	maxRenderIndex: number;
	onBatchRendered?: (done: boolean) => void;
	targetBatchIndex?: number;
}>;

type NodeRendererContextType = ((renderIndex: number) => boolean | undefined) | undefined;
export const NodeRendererContext = createContext<NodeRendererContextType>(undefined);

function useBatchSequence({
	batchSize,
	maxRenderIndex,
	targetBatchIndex = 0,
}: Pick<RootNodeRendererProps, 'batchSize' | 'maxRenderIndex' | 'targetBatchIndex'>) {
	type BatchSequence = {
		// What resets/re-initializes the sequence:
		batchSize: typeof batchSize;
		maxRenderIndex: typeof maxRenderIndex;

		// The sequence:
		batches: Array<number> | undefined;
	};

	const ref = useRef<BatchSequence>();

	if (ref.current?.batchSize !== batchSize || ref.current?.maxRenderIndex !== maxRenderIndex) {
		let batches = ref.current?.batches;

		if (targetBatchIndex) {
			const batchCount = Math.max(Math.ceil(maxRenderIndex / batchSize), 1);

			if (batches?.length !== batchCount) {
				// Generate the sequence of batches to render, starting from `targetBatchIndex`:
				batches = new Array(batchCount);

				let seq = 0;

				batches[targetBatchIndex] = seq++;

				let above = targetBatchIndex - 1;
				let below = targetBatchIndex + 1;
				let yieldAbove = false;

				while (above >= 0 || below < batchCount) {
					if (yieldAbove) {
						if (above >= 0) {
							batches[above] = seq++;
							above--;
						}
					} else if (below < batchCount) {
						batches[below] = seq++;
						below++;
					}

					yieldAbove = !yieldAbove;
				}
			}
		}

		ref.current = {
			batchSize,
			maxRenderIndex,

			batches,
		};
	}

	return ref.current.batches;
}
